(() => {
  const PROXY_URL = '/apps/herlamora-horses/wishlist';
  const LOGIN_REQUIRED_ERROR = 'Customer login required.';

  /* Gemeinsamer Zustand aller Herzen einer Seite. Die Liste wird EINMAL
     geladen: sonst fragte jede Karte einzeln an, und zwei Karten derselben
     Farbe koennten auseinanderlaufen. Jede Antwort der App (GET wie POST)
     enthaelt die vollstaendige Liste und ersetzt den Zustand. */
  const wishlistStore = {
    entries: null,
    pending: null,
    listeners: new Set(),

    /* Der Schluessel ist Produkt + FARBE, nicht die Variante: das Herz sagt
       "diese Farbe ist gemerkt", unabhaengig von der Groesse. Die Produkt-ID
       kommt im Markup nackt und aus der App als GID -- auf den Zahlenteil
       normalisieren, sonst trifft der Vergleich nie. */
    key(productId, color) {
      const id = String(productId || '').split('/').pop();
      return `${id}|${String(color || '').trim().toLowerCase()}`;
    },

    load() {
      if (this.entries) return Promise.resolve(this.entries);
      if (!this.pending) {
        this.pending = fetch(PROXY_URL, {
          headers: { Accept: 'application/json' },
          credentials: 'same-origin',
        })
          .then((response) => (response.ok ? response.json() : null))
          .then((payload) => {
            /* Gast oder Fehler: leere Liste, kein Herz faellt auf "gemerkt"
               zurueck. Der Klick klaert den Login-Fall dann selbst. */
            this.entries = payload && payload.ok && Array.isArray(payload.wishlist) ? payload.wishlist : [];
            return this.entries;
          })
          .catch(() => {
            this.entries = [];
            return this.entries;
          })
          .finally(() => {
            this.pending = null;
          });
      }
      return this.pending;
    },

    set(wishlist) {
      this.entries = Array.isArray(wishlist) ? wishlist : [];
      this.listeners.forEach((listener) => listener());
    },

    matching(productId, color) {
      const wanted = this.key(productId, color);
      return (this.entries || []).filter((entry) => this.key(entry.productId, entry.color) === wanted);
    },

    subscribe(listener) {
      this.listeners.add(listener);
      return () => this.listeners.delete(listener);
    },
  };

  class HerlamoraWishlistPage extends HTMLElement {
    connectedCallback() {
      this.grid = this.querySelector('[data-wishlist-grid]');
      this.empty = this.querySelector('[data-wishlist-empty]');
      this.count = this.querySelector('[data-wishlist-count]');
      this.message = this.querySelector('[data-wishlist-message]');
      this.wishlist = [];

      this.load();
    }

    async load() {
      this.setMessage('Wunschliste wird geladen...', '');

      try {
        const payload = await this.request();
        this.wishlist = this.normalizeWishlist(payload.wishlist);
        this.render();
        this.setMessage('', '');
      } catch (error) {
        this.setMessage(error.message || 'Wunschliste konnte nicht geladen werden.', 'critical');
      }
    }

    async removeProduct(product) {
      try {
        this.setMessage('Produkt wird entfernt...', '');
        const payload = await this.request({
          action: 'remove',
          productId: product.productId,
          variantId: product.variantId,
        });
        this.wishlist = this.normalizeWishlist(payload.wishlist);
        wishlistStore.set(payload.wishlist);
        this.render();
        this.setMessage('Produkt wurde entfernt.', 'success');
      } catch (error) {
        this.setMessage(error.message || 'Produkt konnte nicht entfernt werden.', 'critical');
      }
    }

    render() {
      if (!this.grid) return;

      this.grid.innerHTML = '';
      this.empty.hidden = this.wishlist.length > 0;
      this.count.textContent = this.wishlist.length === 1 ? '1 Produkt gespeichert' : `${this.wishlist.length} Produkte gespeichert`;

      this.wishlist.forEach((product) => {
        this.grid.append(this.renderProduct(product));
      });
    }

    renderProduct(product) {
      const card = document.createElement('article');
      card.className = 'herlamora-wishlist__card';

      const image = product.image?.url
        ? `<img src="${this.escapeHtml(product.image.url)}" alt="${this.escapeHtml(product.image.altText || product.title)}" loading="lazy" width="600" height="750">`
        : '';
      const price = this.formatPrice(product.price);

      card.innerHTML = `
        <a class="herlamora-wishlist__image" href="${this.escapeHtml(product.url)}" aria-label="${this.escapeHtml(product.title)}">
          ${image}
        </a>
        <div class="herlamora-wishlist__content">
          <h2 class="herlamora-wishlist__product-title">
            <a href="${this.escapeHtml(product.url)}">${this.escapeHtml(product.title)}</a>
          </h2>
          ${product.variantTitle ? `<p class="herlamora-wishlist__meta">${this.escapeHtml(product.variantTitle)}</p>` : ''}
          ${price ? `<p class="herlamora-wishlist__meta">${this.escapeHtml(price)}</p>` : ''}
          <div class="herlamora-wishlist__actions">
            <a class="button button--secondary" href="${this.escapeHtml(product.url)}">Ansehen</a>
            <button class="button button--tertiary" type="button" data-wishlist-remove>Entfernen</button>
          </div>
        </div>
      `;

      card.querySelector('[data-wishlist-remove]')?.addEventListener('click', () => {
        this.removeProduct(product);
      });

      return card;
    }

    async request(body) {
      const response = await fetch(PROXY_URL, {
        method: body ? 'POST' : 'GET',
        headers: {
          Accept: 'application/json',
          ...(body ? { 'Content-Type': 'application/json' } : {}),
        },
        credentials: 'same-origin',
        body: body ? JSON.stringify(body) : undefined,
      });

      const payload = await this.parseProxyResponse(response);

      if (!response.ok || !payload.ok) {
        if (this.isLoginRequired(response, payload)) {
          this.redirectToLogin();
        }

        throw new Error(payload.error || 'Wunschliste konnte nicht aktualisiert werden.');
      }

      return payload;
    }

    async parseProxyResponse(response) {
      const contentType = response.headers.get('content-type') || '';

      if (contentType.includes('application/json')) {
        return response.json();
      }

      const body = await response.text();
      const details = body ? ` (${body.slice(0, 120).replace(/\s+/g, ' ')})` : '';

      return {
        ok: false,
        error: `App-Proxy nicht erreichbar: HTTP ${response.status}${details}`,
      };
    }

    isLoginRequired(response, payload) {
      return response.status === 401 || payload?.error === LOGIN_REQUIRED_ERROR;
    }

    redirectToLogin() {
      // Im Theme-Editor niemals wegnavigieren – das lädt die Vorschau neu.
      if (window.Shopify && window.Shopify.designMode) return;

      if (window.__herlamoraCustomerLoginRedirecting) return;

      window.__herlamoraCustomerLoginRedirecting = true;
      const returnTo = `${window.location.pathname}${window.location.search}${window.location.hash}`;
      window.location.assign(`/customer_authentication/login?return_to=${encodeURIComponent(returnTo)}`);
    }

    normalizeWishlist(wishlist) {
      return Array.isArray(wishlist) ? wishlist.filter((product) => product && product.productId) : [];
    }

    formatPrice(price) {
      if (!price?.amount || !price.currencyCode) return '';

      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'de-DE', {
          style: 'currency',
          currency: price.currencyCode,
        }).format(Number(price.amount));
      } catch {
        return `${price.amount} ${price.currencyCode}`;
      }
    }

    setMessage(message, tone) {
      if (!this.message) return;

      this.message.textContent = message;
      this.message.dataset.tone = tone;
    }

    escapeHtml(value) {
      return String(value).replace(/[&<>"']/g, (character) => ({
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#039;',
      }[character]));
    }
  }

  class HerlamoraWishlistButton extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('button');
      this.iconOnly = this.hasAttribute('data-icon-only');
      this.productId = this.dataset.productId;
      this.productHandle = this.dataset.productHandle;
      this.productFormId = this.dataset.productFormId;
      this.addLabel = this.button ? this.currentLabel() : '';
      this.variantColors = this.parseVariantColors();

      this.button?.addEventListener('click', (event) => {
        // Cards wrap their content in a full-card link — cancel the event so
        // clicking the heart never navigates to the product page.
        event.preventDefault();
        event.stopPropagation();
        this.toggle();
      });

      this.unsubscribeStore = wishlistStore.subscribe(() => this.renderState());
      wishlistStore.load().then(() => this.renderState());

      /* Produktseite: die Farbe wechselt mit der Variantenwahl, also den
         Status bei jedem variantChange neu zeichnen. Der Wert selbst wird in
         currentColor() aus der aktuellen Auswahl gelesen -- faellt pubsub
         aus, stimmt beim Klick trotzdem die Farbe, nur die Anzeige haengt. */
      if (this.variantColors && typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribeVariant = subscribe(PUB_SUB_EVENTS.variantChange, () => this.renderState());
      }
    }

    disconnectedCallback() {
      this.unsubscribeStore?.();
      this.unsubscribeVariant?.();
    }

    parseVariantColors() {
      if (!this.dataset.variantColors) return null;
      try {
        return JSON.parse(this.dataset.variantColors);
      } catch {
        return null;
      }
    }

    currentLabel() {
      if (this.iconOnly) return this.button.getAttribute('aria-label') || '';
      return this.button.querySelector('[data-wishlist-label]')?.textContent || this.button.textContent || '';
    }

    /* Karten tragen ihre Farbe fest im Markup; die Produktseite leitet sie
       aus der gewaehlten Variante ab. */
    currentColor() {
      if (this.variantColors) {
        const variantId = String(this.dataset.variantId || this.selectedVariantId() || '').split('/').pop();
        const mapped = variantId ? this.variantColors[variantId] : undefined;
        if (mapped !== undefined) return mapped;
      }
      return this.dataset.color || '';
    }

    savedEntries() {
      return wishlistStore.matching(this.productId, this.currentColor());
    }

    renderState() {
      if (!this.button) return;

      const saved = this.savedEntries().length > 0;
      this.button.setAttribute('aria-pressed', saved ? 'true' : 'false');
      this.setLabel(saved ? this.dataset.savedLabel || 'Auf der Wunschliste' : this.addLabel);
    }

    setLabel(text) {
      if (!this.button) return;

      if (this.iconOnly) {
        this.button.setAttribute('aria-label', text);
        this.button.title = text;
        return;
      }

      const label = this.button.querySelector('[data-wishlist-label]');

      if (label) {
        label.textContent = text;
      } else {
        this.button.textContent = text;
      }
    }

    /* Ein Klick schaltet die FARBE um. Ist sie gemerkt, fliegt sie ganz
       heraus -- auch mit mehreren Groessen, und zwar in EINEM Aufruf: eine
       Anfrage je Variante wuerde das Metafeld gegen sich selbst rennen
       lassen. */
    async toggle() {
      if (!this.button || (!this.productId && !this.productHandle)) return;

      await wishlistStore.load();
      const saved = this.savedEntries();
      const removing = saved.length > 0;

      this.button.disabled = true;
      this.setLabel(
        removing
          ? this.dataset.removingLabel || 'Wird entfernt...'
          : this.dataset.loadingLabel || 'Wird gespeichert...'
      );

      const body = removing
        ? { action: 'remove', variantIds: saved.map((entry) => entry.variantId) }
        : {
            action: 'add',
            productId: this.productId,
            variantId: this.dataset.variantId || this.selectedVariantId(),
            handle: this.productHandle,
          };

      try {
        const response = await fetch(PROXY_URL, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          credentials: 'same-origin',
          body: JSON.stringify(body),
        });
        const payload = await this.parseProxyResponse(response);

        if (!response.ok || !payload.ok) {
          if (this.isLoginRequired(response, payload)) {
            this.redirectToLogin();
          }

          throw new Error(payload.error || 'Wunschliste konnte nicht aktualisiert werden.');
        }

        this.button.disabled = false;
        // Die Antwort traegt die vollstaendige Liste — daraus zeichnen sich
        // alle Herzen der Seite neu, auch weitere Karten derselben Farbe.
        wishlistStore.set(payload.wishlist);
      } catch {
        this.button.disabled = false;
        this.setLabel(this.dataset.errorLabel || 'Erneut versuchen');
      }
    }

    selectedVariantId() {
      const scopedInput = this.productFormId
        ? document.querySelector(`#${CSS.escape(this.productFormId)} input[name="id"]`)
        : null;
      const nearbyInput = this.closest('product-info')?.querySelector('input[name="id"]');
      const productFormInput = document.querySelector('product-form input[name="id"]');

      return scopedInput?.value || nearbyInput?.value || productFormInput?.value || '';
    }

    async parseProxyResponse(response) {
      const contentType = response.headers.get('content-type') || '';

      if (contentType.includes('application/json')) {
        return response.json();
      }

      const body = await response.text();
      const details = body ? ` (${body.slice(0, 120).replace(/\s+/g, ' ')})` : '';

      return {
        ok: false,
        error: `App-Proxy nicht erreichbar: HTTP ${response.status}${details}`,
      };
    }

    isLoginRequired(response, payload) {
      return response.status === 401 || payload?.error === LOGIN_REQUIRED_ERROR;
    }

    redirectToLogin() {
      // Im Theme-Editor niemals wegnavigieren – das lädt die Vorschau neu.
      if (window.Shopify && window.Shopify.designMode) return;

      if (window.__herlamoraCustomerLoginRedirecting) return;

      window.__herlamoraCustomerLoginRedirecting = true;
      const returnTo = `${window.location.pathname}${window.location.search}${window.location.hash}`;
      window.location.assign(`/customer_authentication/login?return_to=${encodeURIComponent(returnTo)}`);
    }
  }

  if (!customElements.get('herlamora-wishlist-page')) {
    customElements.define('herlamora-wishlist-page', HerlamoraWishlistPage);
  }

  if (!customElements.get('herlamora-wishlist-button')) {
    customElements.define('herlamora-wishlist-button', HerlamoraWishlistButton);
  }
})();
